import React from 'react'
import { Box, Typography, Paper, List, ListItem, ListItemText, Switch, CircularProgress, Alert } from '@mui/material'
import useAccount from '../hooks/useAccount'
import useRouter from '../hooks/useRouter'
import useMySubscriptions from '../hooks/useMySubscriptions'
import { DashboardSubscriber } from '../types/gotypes'

// MySubscriptionsPage lists every dashboard the current user watches (#964
// v1) — active and tombstoned alike — with a toggle that calls the same
// self-subscribe/self-unsubscribe routes as the notify dialog. User
// preference always wins here: toggling off is permanent until the user
// toggles it back on themselves.
const MySubscriptionsPage: React.FC = () => {
  const account = useAccount()
  const router = useRouter()
  const customer = account.customer?.name || ''
  const { subscriptions, isLoading, isError, unsubscribe, resubscribe, toggleIsLoading } = useMySubscriptions({ customer })

  const handleToggle = (sub: DashboardSubscriber) => {
    if (sub.unsubscribed_at) {
      resubscribe(sub.dashboard_id)
    } else {
      unsubscribe(sub.dashboard_id)
    }
  }

  return (
    <Box sx={{ p: 3, maxWidth: 720, mx: 'auto' }}>
      <Typography variant="h5" sx={{ mb: 2 }}>My subscriptions</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Dashboards you're watching. When a dashboard owner notifies subscribers, you'll see it in your
        notification bell — turn a dashboard off here any time.
      </Typography>

      {isLoading && <CircularProgress size={24} />}
      {isError && <Alert severity="error">Failed to load your subscriptions.</Alert>}

      {!isLoading && !isError && subscriptions.length === 0 && (
        <Paper elevation={0} sx={{ p: 4, textAlign: 'center', border: '1px dashed', borderColor: 'grey.300' }}>
          <Typography variant="body1" color="text.secondary">
            You aren't subscribed to any dashboards yet.
          </Typography>
        </Paper>
      )}

      {!isLoading && !isError && subscriptions.length > 0 && (
        <Paper elevation={1}>
          <List disablePadding>
            {subscriptions.map(sub => (
              <ListItem
                key={sub.id}
                divider
                secondaryAction={
                  <Switch
                    checked={!sub.unsubscribed_at}
                    disabled={toggleIsLoading}
                    onChange={() => handleToggle(sub)}
                    color="info"
                  />
                }
              >
                <ListItemText
                  primary={
                    <Box
                      component="span"
                      sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                      onClick={() => router.navigate('dashboard-view', { customer: sub.customer, dashboardId: sub.dashboard_id })}
                    >
                      {sub.dashboard_id}
                    </Box>
                  }
                  secondary={sub.unsubscribed_at ? 'Unsubscribed' : `Subscribed by ${sub.added_by}`}
                />
              </ListItem>
            ))}
          </List>
        </Paper>
      )}
    </Box>
  )
}

export default MySubscriptionsPage
